import {useEffect, useRef} from "react";
import {FiAlertCircle} from "react-icons/fi";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {FilterCategory, FilterSets} from "@/components/filterDropdown/FilterDropdown.ts";
import {KPIIconTypes} from "@/components/KPIIcon/KPIIcon.types.ts";
import {TooltipIconRA} from "@/components/overlay/TooltipIconRA.tsx";
import {SummaryBar} from "@/components/summaryBar/SummaryBar.tsx";
import {SummaryBlock} from "@/components/summaryBar/SummaryBar.types.ts";
import {GlobalAlertStateStatsResponse} from "@/lib/queries/alerting.ts";
import {AccountSubscriptionType} from "@/lib/types/accounts.ts";
import {LimitCheckPhoneSmsAlertLimit} from "@/pages/billing/v2/InstantCheckout/LimitCheckPhoneSmsAlertLimit.tsx";
import {LimitCheckerComponent} from "@/pages/billing/v2/InstantCheckout/LimitCheckWithInstantCheckout.tsx";

interface AlertsSummaryBarProps {
  stats: GlobalAlertStateStatsResponse;
  filterSets: FilterSets<FilterCategory>;
  onFilterSetChange: (filterSet: FilterSets<FilterCategory>) => void;
}

export const AlertsSummaryBar = ({stats, filterSets, onFilterSetChange}: AlertsSummaryBarProps) => {
  const {user} = useCurrentUserContext();
  const isBilling2 = user?.account.subscription_type === AccountSubscriptionType.BILLING_2_0;
  const hasSubscription = !user?.account.free_trial_expires_at;
  const phoneSmsAlertsLimitCheckRef = useRef<LimitCheckerComponent>();
  const summaryBlocks: SummaryBlock<GlobalAlertStateStatsResponse>[] = [
    {
      applyFilterset: {status: ["down"]},
      title: "Ongoing Alerts",
      icon: KPIIconTypes.ALERTS_ACTIVE,
      renderStat: (stats) => <>{stats.down}</>,
    },
    {
      applyFilterset: {status: ["resolved"]},
      title: "Resolved Alerts",
      icon: KPIIconTypes.ALERTS_RESOLVED,
      renderStat: (stats) => <>{stats.resolved}</>,
    },
    {
      applyFilterset: {status: ["ignored"]},
      title: "Ignored Alerts",
      icon: KPIIconTypes.ALERTS_IGNORED,
      renderStat: (stats) => <>{stats.ignored}</>,
    },
  ];
  if (isBilling2) {
    summaryBlocks.push({
      applyFilterset: {status: ["ignored"]},
      title: "Alerts Used",
      renderStat: (stats) => {
        const alertsUsed = stats.sms_and_phone_alerts_used || 0;
        const alertsAvail = stats.sms_and_phone_alerts_available || 0;
        return (
          <div className="d-flex">
            {alertsUsed} / {alertsAvail}
            {alertsUsed >= alertsAvail && (
              <TooltipIconRA
                className="ms-1 pulse-color-red-500"
                title={`You have reached the max. amount of Alerts allocated to your account (${alertsUsed}/${alertsAvail}).`}
                icon={FiAlertCircle}
                placement={"bottom"}
              />
            )}
            <LimitCheckPhoneSmsAlertLimit ref={phoneSmsAlertsLimitCheckRef} />
          </div>
        );
      },
    });
  }
  useEffect(() => {
    if (hasSubscription) {
      phoneSmsAlertsLimitCheckRef.current?.checkIfLimitHit();
    }
  }, [hasSubscription]);
  return (
    <SummaryBar
      stats={stats}
      summaryBlocks={summaryBlocks}
      className={"mb-3"}
      columns={
        isBilling2
          ? {
              sm: 1,
              md: 2,
              lg: 2,
              xl: 4,
            }
          : undefined
      }
      filterSets={filterSets}
      onFilterSetChange={onFilterSetChange}
    />
  );
};
